import { filter } from 'lodash'
import { fork, takeEvery, takeLatest, take, race, delay, select, all, call, put } from 'redux-saga/effects'
import requestWithRetry from 'civic-champs-shared/api/requestWithRetry'

import { isFeatureEnabled } from '../../core/feature/selectors'
import { KIOSK_SESSION_LIFECYCLE_FEATURE_KEY } from '../kioskSessionLifecycleFeature'
import { getEncodedOccurrence, getOrganizationId, getTimesheetMode } from '../selectors'
import { reportRosterFailed, reportRosterLoaded, resetRosterDiagnostics } from '../kioskDiagnostics'
import {
  GET_VOLUNTEERS,
  GET_VOLUNTEERS_SUCCEEDED,
  GET_VOLUNTEERS_FAILED,
  getVolunteers,
  getVolunteersSucceeded,
  getVolunteersFailed,
  KIOSK_STARTED,
  KIOSK_STOPPED,
  SEARCH_VOLUNTEERS_REQUESTED,
  searchVolunteersSucceeded,
  searchVolunteersFailed,
  searchVolunteersCleared,
} from '../actions'
import processRawVolunteers from './utils/processRawVolunteers'

const BASE_POLL_MS = 1000 * 60 * 5
const MAX_POLL_MS = 1000 * 60 * 30

export default function* volunteersSaga() {
  yield fork(watchGetVolunteers)
  yield fork(synchronizeVolunteersFlow)
  yield fork(watchSearchVolunteers)
}

export function* synchronizeVolunteersFlow() {
  while (true) {
    yield take(KIOSK_STARTED)
    yield call(resetRosterDiagnostics)
    yield race([
      take(KIOSK_STOPPED),
      call(function* pollVolunteersWithBackoff() {
        let pollDelayMs = BASE_POLL_MS
        while (true) {
          const lifecycleEnabled = yield select(state => isFeatureEnabled(state, KIOSK_SESSION_LIFECYCLE_FEATURE_KEY))
          yield put(getVolunteers())
          if (lifecycleEnabled) {
            // Backoff between poll cycles on failure; axios retry disabled on fetch (see fetchVolunteers).
            const result = yield race({
              success: take(GET_VOLUNTEERS_SUCCEEDED),
              failed: take(GET_VOLUNTEERS_FAILED),
            })
            pollDelayMs = result.failed ? Math.min(pollDelayMs * 2, MAX_POLL_MS) : BASE_POLL_MS
          } else {
            pollDelayMs = BASE_POLL_MS
          }
          yield delay(pollDelayMs)
        }
      }),
    ])
  }
}

export function* watchGetVolunteers() {
  yield takeEvery(GET_VOLUNTEERS, onGetVolunteers)
}

export function* onGetVolunteers() {
  // Gathered up front so a diagnostics problem can never be mistaken for a roster
  // failure by the catch below.
  const context = yield call(collectRosterContext)
  const startedAt = Date.now()

  try {
    let [allVolunteers, activeVolunteers] = yield all([fetchVolunteers(), fetchActiveVolunteers()])
    let volunteers = processRawVolunteers(allVolunteers, activeVolunteers)

    yield put(getVolunteersSucceeded(volunteers))
    yield call(reportRosterLoaded, {
      ...context,
      rosterCount: volunteers.length,
      checkedInCount: filter(volunteers, volunteer => volunteer.checkedInAt != null).length,
      durationMs: Date.now() - startedAt,
    })
  } catch (error) {
    yield put(getVolunteersFailed(error))
    yield call(reportRosterFailed, { ...context, error, durationMs: Date.now() - startedAt })
  }
}

function* collectRosterContext() {
  return {
    organizationId: yield select(getOrganizationId),
    encodedOccurrence: yield select(getEncodedOccurrence),
    mode: yield select(getTimesheetMode),
  }
}

function* fetchVolunteers() {
  let encodedOccurrence = yield select(getEncodedOccurrence)
  let url = `/opportunity_instances/${encodedOccurrence}/volunteer_roster`
  const useRetry = !(yield select(state => isFeatureEnabled(state, KIOSK_SESSION_LIFECYCLE_FEATURE_KEY)))
  return yield call(requestWithRetry, { url }, useRetry)
}

function* fetchActiveVolunteers() {
  let encodedOccurrence = yield select(getEncodedOccurrence)
  let url = `/opportunity_instances/${encodedOccurrence}/active_volunteers`
  const useRetry = !(yield select(state => isFeatureEnabled(state, KIOSK_SESSION_LIFECYCLE_FEATURE_KEY)))
  return yield call(requestWithRetry, { url }, useRetry)
}

export function* watchSearchVolunteers() {
  yield takeLatest(SEARCH_VOLUNTEERS_REQUESTED, onSearchVolunteers)
}

export function* onSearchVolunteers(action) {
  const query = (action.payload && action.payload.query) || ''
  const trimmed = query.trim()
  if (!trimmed) {
    yield put(searchVolunteersCleared())
    return
  }
  try {
    const encodedOccurrence = yield select(getEncodedOccurrence)
    if (!encodedOccurrence) {
      yield put(searchVolunteersSucceeded(query, []))
      return
    }
    const url = `/opportunity_instances/${encodedOccurrence}/volunteer_search?q=${encodeURIComponent(trimmed)}&limit=20`
    const results = yield call(requestWithRetry, { url })
    yield put(searchVolunteersSucceeded(query, results || []))
  } catch (error) {
    yield put(searchVolunteersFailed(query, error))
  }
}
